import _ from "lodash";
import {
  findQuestionById,
  findSectionById,
} from "src/services/formTemplate/v3";
import * as AF from "src/types/formTemplate";

export function hasChanges(
  original: AF.V3.FormTemplate,
  draft: AF.V3.FormTemplate
): boolean {
  if (original.sections.length !== draft.sections.length) {
    return true;
  }

  for (let i = 0; i < original.sections.length; i++) {
    const originalSection = original.sections[i];
    const draftSection = draft.sections[i];

    if (!originalSection || !draftSection) return true;
    if (Section.hasChanges(originalSection, draftSection)) return true;
    if (!("questions" in originalSection && "questions" in draftSection))
      continue;
    if (originalSection.questions.length !== draftSection.questions.length)
      return true;
    for (let j = 0; j < originalSection.questions.length; j++) {
      const originalQuestion = originalSection.questions[j];
      const draftQuestion = draftSection.questions[j];
      if (!originalQuestion || !draftQuestion) return true;
      if (Question.hasChanges(originalQuestion, draftQuestion)) return true;
    }
  }

  return false;
}

export const Question = {
  isUnsaved: (
    questionId: string,
    original: AF.V3.FormTemplate,
    draft: AF.V3.FormTemplate
  ): boolean => {
    const originalQuestion = findQuestionById(original, questionId);
    const draftQuestion = findQuestionById(draft, questionId);
    if (!originalQuestion) {
      // this is a new unsaved question
      return true;
    }
    if (!draftQuestion) {
      // this is invalid/deleted question
      return false;
    }

    return Question.hasChanges(originalQuestion, draftQuestion);
  },
  hasChanges: (original: AF.V3.Question, draft: AF.V3.Question): boolean => {
    return !_.isEqual(_.omitBy(original, _.isNil), _.omitBy(draft, _.isNil));
  },
};

export const Section = {
  isUnsaved: (
    sectionId: string,
    original: AF.V3.FormTemplate,
    draft: AF.V3.FormTemplate
  ): boolean => {
    const [originalSection, originalIndex] = findSectionById(
      original,
      sectionId
    );
    const [draftSection, draftIndex] = findSectionById(draft, sectionId);
    if (!originalSection) {
      // this is a new unsaved section
      return true;
    }
    if (!draftSection) {
      // this is invalid/deleted section
      return false;
    }

    if (originalIndex !== draftIndex) {
      // section is moved
      return true;
    }

    return Section.hasChanges(originalSection, draftSection);
  },

  isNew: (
    sectionId: string,
    original: AF.V3.FormTemplate,
    draft: AF.V3.FormTemplate
  ): boolean => {
    const [originalSection] = findSectionById(original, sectionId);
    if (!originalSection) {
      // this is a new unsaved section
      return true;
    }
    return false;
  },
  hasChanges: (original: AF.V3.Section, draft: AF.V3.Section): boolean => {
    return (
      hasSectionChanges(original, draft) ||
      hasQuestionOrderChanges(original, draft)
    );
  },
};

const hasSectionChanges = (
  original: AF.V3.Section,
  draft: AF.V3.Section
): boolean => {
  return !_.isEqual(
    _.omit(_.omitBy(original, _.isNil), ["questions"]),
    _.omit(_.omitBy(draft, _.isNil), ["questions"])
  );
};

const hasQuestionOrderChanges = (
  original: AF.V3.Section,
  draft: AF.V3.Section
): boolean => {
  if (!("questions" in original && "questions" in draft)) return false;
  if (original.questions.length !== draft.questions.length) return true;
  for (let i = 0; i < original.questions.length; i++) {
    const originalQuestionId = original.questions[i]?.id;
    const draftQuestionId = draft.questions[i]?.id;
    if (originalQuestionId !== draftQuestionId) return true;
  }

  return false;
};

export function getNewVerificationId(): string {
  return crypto.randomUUID();
}

// placeholder ID for a new verification
export const NEW_VERIFICATION_ID = "new-verification-id";

export function isNewVerification(
  verification: AF.V3.FormVerification
): boolean {
  return verification.id === NEW_VERIFICATION_ID;
}
